import { Injectable } from '@angular/core';
import { HttpClientService } from '../core/services/http-client.service';
import { ICollege, ICollegeTrack } from '../interfaces';
import { map } from 'rxjs/operators';
import { DateUtility } from 'src/app/utils/date-utility';
import { cacheBustCollegeMedia, cacheBustCollegeMediaList } from 'src/app/utils/cache-bust-url.util';
import { VeteranProfile } from '../models';

@Injectable({
  providedIn: 'root',
})
export class CollegeService {
  constructor(private httpClient: HttpClientService) {}

  getAdminSetting(name: string) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient.get(`/admin/setting/${name}?dt=${newdate}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getAutoCollegeListForReview(keyword: string) {
    return this.httpClient
      .get(`/calculator/college-list?keyword=${keyword}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getAutoCollegeList(searchText: string) {
    return this.httpClient.post(`/college/autolist`, { name: searchText }).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getDefaultCollege() {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient.get(`/college/default?dt=${newdate}`).pipe(
      map((response: any) => {
        return cacheBustCollegeMediaList(response.data.collegelist);
      }),
    );
  }

  getActiveCollegeLists() {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient.get(`/superadmin/collegelist?dt=${newdate}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getCollegeComparisonDetail(collegeId: number, studentId: any) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient
      .get(`/college/comparison/${collegeId}/${studentId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getNameFilterData(name: string) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient
      .get(`/filterby/name/datalist`, { name, dt: newdate })
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  searchCollegeByFilters(filterParams: any) {
    return this.httpClient.post(`/college/search`, filterParams).pipe(
      map((response: any) => {
        const data = response.data;
        if (!data?.collegelist?.length) {
          return data;
        }

        const version = Date.now();
        return {
          ...data,
          collegelist: cacheBustCollegeMediaList(data.collegelist, version),
        };
      }),
    );
  }

  collegeTracking(body: ICollegeTrack) {
    return this.httpClient.post('/college/search/tracking', body).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  bookmarkTracking(body: any) {
    return this.httpClient.post(`/college/bookmark/tracking`, body).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  filterTracking(data: any) {
    return this.httpClient.post(`/college/filter/tracking`, data).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  buttonClickTracking(body: any) {
    return this.httpClient.post(`/college/button/tracking`, body).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getCollegeIdFromCollegeAlias(collegeAlias: string) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient
      .get(`/collegealias/collegeid/${collegeAlias}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getBucketSpecificCollegeData(alias: string) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient
      .get(`/college/bucketspecificcollegedata/${alias}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getCollegeDetail(collegeId: any) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient.get(`/college/${collegeId}?dt=${newdate}`).pipe(
      map((response: any) => {
        return cacheBustCollegeMedia(response.data);
      }),
    );
  }

  getCollegeMetaDataInfo(collegeId: number) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient
      .get(`/college/metadata/${collegeId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getParentChildCollegeInfo(collegeId: number, parentId: any) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient
      .get(
        `/college/multicampus/datalist/${collegeId}/${parentId}?dt=${newdate}`,
      )
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getParentChildCollegeLists(collegeId: number, parentId: any, states: string[] = []) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    const stateParam = states.length ? `&state=${encodeURIComponent(states.join(','))}` : '';
    return this.httpClient
      .get(
        `/college/multicampus/collegelist/${collegeId}/${parentId}?dt=${newdate}${stateParam}`,
      )
      .pipe(
        map((response: any) => {
          return cacheBustCollegeMediaList(response.data);
        }),
      );
  }

  getYellowRibbonData(collegeId: number) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient
      .get(`/college/yellowribbon/data/${collegeId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  updateYellowRibbonData(data: any) {
    return this.httpClient.post(`/college/update/yellowribbondata`, data).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getContactInfo(contactData: any) {
    return this.httpClient
      .post(`/college/contactinfo/tracking`, contactData)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getSimilarSchoolLists(collegeData: any, omitTracking = false) {
    return this.httpClient
      .post(
        `/college/nonpartner/similarschool?track=${omitTracking}`,
        collegeData,
      )
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getMatchedPercentOfCollege(studentId: string, collegeId: any) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient
      .get(`/college/matched-percent/${studentId}/${collegeId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  saveMatchedCollegeData(matchData: any) {
    return this.httpClient
      .post(`/register/match/save/collegelist`, matchData)
      .pipe(
        map((response: any) => {
          return response;
        }),
      );
  }

  getMajorByCollegeAndLevel(collegeid: any, levelid: any) {
    const newdate = DateUtility.format(new Date(), 'MMddyyy_HHmmss');
    return this.httpClient
      .get(`/majors/college/${collegeid}/level/${levelid}/?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  updateCollegeMajors(model: any, collegeId: string) {
    return this.httpClient
      .post(`/college/update/collegeMajordata/${collegeId}`, model)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getCollegeReviews(collegeid: any, studentId?: string) {
    const url = studentId ? `/college/review/datalist/${collegeid}?studentId=${studentId}` : `/college/review/datalist/${collegeid}`;
    return this.httpClient.get(url).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  // getVeteranCollegeReview(studentId: any, collegeId: any) {
  //   const newdate = DateUtility.format(new Date(), 'MMddyyy_HHmmss');
  //   return this.httpClient
  //     .get(
  //       `/veteran/check/collegereview/${studentId}/${collegeId}/?dt=${newdate}`,
  //     )
  //     .pipe(
  //       map((response: any) => {
  //         return response.data;
  //       }),
  //     );
  // }

  addReview(reviewModel: any) {
    return this.httpClient.post('/add/review', reviewModel).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getReviewById(reviewId: string) {
    return this.httpClient.get(`/review/${reviewId}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  updateReview(reviewModel: any, reviewId: string) {
    return this.httpClient.put(`/update/review/${reviewId}`, reviewModel).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getInboxMessage(collegeId: number, type: string) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient
      .get(`/reconmessage/college/inbox/${collegeId}/${type}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  replyMessage(message: any) {
    return this.httpClient.post(`/college/replymessage`, message).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  deleteMessage(message: any) {
    return this.httpClient.post(`/college/deletemessage`, message).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getInboxMessageCount(collegeId: number) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient
      .get(`/reconmessage/college/inboxcount/${collegeId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getBasicCollegeInfo(collegeId: number) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient
      .get(`/college/basicinfo/${collegeId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getCollegeActivityReport(collegeId: number) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient
      .get(`/college/admin/activity/${collegeId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getNextCollegeActivityReport(collegeId: number) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient
      .get(`/college/admin/activity/report/${collegeId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getCollegeCampaignList(collegeId: number) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient
      .get(`/college/campaign/datalist/${collegeId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getCollegeCampaignDetail(collegeId: number, campaignId: number) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient
      .get(`/college/campaign/detail/${collegeId}/${campaignId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getCampaignRangeData(data: any) {
    return this.httpClient.post(`/college/campaign/rangedata`, data).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getCollegeCampaignNonPaid(collegeId: any) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient
      .get(`/college/campaign/paid/nonpaid/${collegeId}?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getCollegeAdminSchoolData(reportData: any) {
    return this.httpClient
      .post(`/superadmin/registerfrequency/school/data`, reportData)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getCollegeAdminPlatformData(reportData: any) {
    return this.httpClient
      .post(
        `/superadmin/searchfrequency/platform/data
      `,
        reportData,
      )
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  updateLogoImage(logoinfo: any) {
    return this.httpClient.post(`/college/update/logo`, logoinfo).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  updateBannerImage(bannerInfo: any) {
    return this.httpClient.post(`/college/update/banner`, bannerInfo).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  updateCampusImage(logoinfo: any) {
    return this.httpClient.post(`/college/update/photo`, logoinfo).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  updateCollege(model: any) {
    return this.httpClient.post(`/college/update/overviewtext`, model).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  updateCollegeStats(model: any) {
    return this.httpClient.post(`/college/update/collegeoverview`, model).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getStudentMessageInfo(studentid: any) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient
      .get('/student/messageinfo/' + studentid + '?dt=' + newdate)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getSuperAdminInfo(id: any) {
    return this.httpClient.get('/superadmin/list/' + id).pipe(
      map((response: any) => {
        return response;
      }),
    );
  }

  getVeteranCollegeReviewData(studentid: any) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient
      .get('/veteran/college/reviewlist/' + studentid + '?dt=' + newdate)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getCollegeAliasFromId(id: any) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient
      .get('/collegeid/collegealias/' + id + '?dt=' + newdate)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getVeteranFilter(studentid: any) {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient
      .get('/student/filter/' + studentid + '?dt=' + newdate)
      .pipe(
        map((response: any) => {
          let results: VeteranProfile;
          results = response.data;
          return results;
        }),
      );
  }

  getActiveCollegeCount() {
    const newdate = DateUtility.format(new Date(), 'MMddyyyy_HHmmss');
    return this.httpClient
      .get(`/superadmin/active/collegecount?dt=${newdate}`)
      .pipe(
        map((response: any) => {
          return response.data;
        }),
      );
  }

  getAdvertisementLists(type: string) {
    return this.httpClient.get(`/superadmin/advertise/${type}`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }

  getSitemapData() {
    return this.httpClient.get(`/sitemap`).pipe(
      map((response: any) => {
        return response.data;
      }),
    );
  }
}
